import { HTTPService } from './HttpService';
import { Photo } from '../types';
import { createLogger } from '../utils/logger';

const logger = createLogger('[AdminService]');

export interface ConfigResponse {
  id: number;
  key: string;
  value: string;
  created: string;
  updated: string;
}

export interface SortPhotosResponse {
  count: number;
  message: string;
}

export class AdminService {
  constructor(private httpService: HTTPService) {}

  /**
   * Sort photos by randomizing their order and storing in backend config
   * Admin-only endpoint: POST /api/admin/photos/sort
   * @returns Response with photo count sorted
   */
  async sortPhotos(): Promise<SortPhotosResponse> {
    try {
      logger.debug('sortPhotos: calling POST /admin/photos/sort');
      const response = await this.httpService.post('/admin/photos/sort');
      const config: ConfigResponse = response.data;
      
      // Count the number of photo IDs (comma-separated in value field)
      const count = config.value ? config.value.split(',').length : 0;
      
      logger.info(`sortPhotos: success - ${count} photos sorted`);
      return {
        count,
        message: `${count} photos randomized`,
      };
    } catch (error) {
      logger.error('sortPhotos: failed -', error);
      throw error;
    }
  }

  /**
   * Roll back a photo to its pre-replace backup, undoing the last enhancement.
   * Admin-only endpoint: POST /api/admin/photos/{id}/rollback
   */
  async rollbackPhoto(photoId: number): Promise<Photo> {
    try {
      logger.debug(`rollbackPhoto: calling POST /admin/photos/${photoId}/rollback`);
      const response = await this.httpService.post(`/admin/photos/${photoId}/rollback`);
      return response.data;
    } catch (error) {
      logger.error('rollbackPhoto: failed -', error);
      throw error;
    }
  }

  /**
   * Permanently delete a photo's pre-replace backup (S3 objects + DB pointers)
   * without touching the live photo.
   * Admin-only endpoint: DELETE /api/admin/photos/{id}/backup
   */
  async deletePhotoBackup(photoId: number): Promise<Photo> {
    try {
      logger.debug(`deletePhotoBackup: calling DELETE /admin/photos/${photoId}/backup`);
      const response = await this.httpService.delete(`/admin/photos/${photoId}/backup`);
      return response.data;
    } catch (error) {
      logger.error('deletePhotoBackup: failed -', error);
      throw error;
    }
  }

  /**
   * Permanently delete a photo: DB row + every S3 object it owns. No undo.
   * Admin-only endpoint: DELETE /api/admin/photos/{id}
   */
  async hardDeletePhoto(photoId: number): Promise<void> {
    try {
      logger.debug(`hardDeletePhoto: calling DELETE /admin/photos/${photoId}`);
      await this.httpService.delete(`/admin/photos/${photoId}`);
    } catch (error) {
      logger.error('hardDeletePhoto: failed -', error);
      throw error;
    }
  }
}
